import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import TierBadge, { TIER_STYLE } from '../components/TierBadge'

const STATUS_STYLE = {
  'open':        { bg: '#FCEBEB', color: '#A32D2D' },
  'scheduled':   { bg: '#F1EFE8', color: '#5F5E5A' },
  'dispatched':  { bg: '#FAEEDA', color: '#854F0B' },
  'remote':      { bg: '#E6F1FB', color: '#185FA5' },
  'in_progress': { bg: '#FAEEDA', color: '#854F0B' },
  'blocked':     { bg: '#FCEBEB', color: '#A32D2D' },
  'resolved':    { bg: '#EAF3DE', color: '#3B6D11' },
  'cancelled':   { bg: '#F1EFE8', color: '#5F5E5A' },
}

export default function Tickets() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [tickets, setTickets] = useState([])
  const [programs, setPrograms] = useState([])
  const [sites, setSites] = useState([])
  const [loading, setLoading] = useState(true)
  const [programFilter, setProgramFilter] = useState('all')
  const [tierFilter, setTierFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('open') // default: show only open-ish
  const [assigneeFilter, setAssigneeFilter] = useState('all') // 'all' | 'mine' — filter to tickets assigned to the current user
  const [sortMode, setSortMode] = useState('priority') // 'priority' (default from DB order) | 'recent' (newest first)
  const [search, setSearch] = useState('')
  const [showNewModal, setShowNewModal] = useState(false)

  // Only admin / executive / regional_ops can insert tickets under the
  // existing 'ops write tickets' RLS policy. Anyone else gets the button
  // hidden rather than a confusing 403 on submit.
  const canCreate = ['admin', 'executive', 'regional_ops'].includes(profile?.role)

  useEffect(() => { fetchTickets() }, [])

  async function fetchTickets() {
    const [ticketsRes, progRes, sitesRes] = await Promise.all([
      supabase.from('tickets')
        .select('*, sites(id, name), programs(id, slug, name, color)')
        .order('priority_rank', { ascending: false })
        .order('created_at',    { ascending: false }),
      supabase.from('programs').select('id, slug, name, color').order('sort_order'),
      supabase.from('sites').select('id, name, city, state').eq('active', true).order('name'),
    ])
    setTickets(ticketsRes.data || [])
    setPrograms(progRes.data || [])
    setSites(sitesRes.data || [])
    setLoading(false)
  }

  async function createTicket(form, imageFiles) {
    const payload = {
      site_id:       form.site_id,
      program_id:    form.program_id || null,
      title:         form.title.trim(),
      description:   form.description?.trim() || null,
      tier:          form.tier || null,
      type:          form.type || null,
      assignee_team: form.assignee_team || null,
      status:        'open',
      source:        'manual',
      created_by:    profile?.id || null,
    }
    // Ask Supabase to return the new row so we can attach images to it
    // before returning to the user. RLS doesn't normally require this —
    // it's just how we get the generated id back in one round-trip.
    const { data: created, error } = await supabase
      .from('tickets').insert(payload).select('id').single()
    if (error) { alert('Create failed: ' + error.message); return }

    // Upload any attached images. We do this client-side inside the same
    // call so the user doesn't have to navigate to the ticket detail page
    // just to add their photos. Each upload is a separate storage write +
    // metadata insert; failures surface as an alert and abort the rest,
    // but the ticket itself is already created at this point.
    if (imageFiles && imageFiles.length > 0 && profile?.id) {
      for (const file of imageFiles) {
        if (!file.type.startsWith('image/')) continue
        const ext = file.name.split('.').pop() || 'jpg'
        const path = `${profile.id}/${created.id}/${Date.now()}-${Math.random().toString(36).slice(2,8)}.${ext}`
        const { error: upErr } = await supabase.storage.from('ticket-images').upload(path, file)
        if (upErr) { alert(`Uploaded ticket but image failed: ${upErr.message}`); break }
        const { error: rowErr } = await supabase.from('ticket_images')
          .insert({ ticket_id: created.id, storage_path: path, uploaded_by: profile.id })
        if (rowErr) {
          await supabase.storage.from('ticket-images').remove([path])
          alert(`Uploaded ticket but image row failed: ${rowErr.message}`); break
        }
      }
    }

    setShowNewModal(false)
    fetchTickets()
  }

  if (loading) return <div className="spinner" />

  const activeStatuses = ['open','scheduled','dispatched','remote','in_progress','blocked']
  const filtered = tickets.filter(t => {
    if (programFilter !== 'all' && t.programs?.slug !== programFilter) return false
    if (tierFilter !== 'all' && t.tier !== tierFilter) return false
    if (statusFilter === 'open' && !activeStatuses.includes(t.status)) return false
    if (statusFilter === 'resolved' && t.status !== 'resolved') return false
    // "Mine" = tickets I need to act on. Matches in two ways:
    //   (1) Directly assigned to me via assignee_id.
    //   (2) Routed to my team (profile.team === ticket.assignee_team) AND
    //       no specific person has been assigned yet (assignee_id is null) —
    //       i.e. in my team's unclaimed queue. If a specific teammate has
    //       been picked, it's their Mine, not mine.
    if (assigneeFilter === 'mine') {
      const isDirect = t.assignee_id && t.assignee_id === profile?.id
      const isMyTeamUnclaimed =
        profile?.team && t.assignee_team === profile.team && !t.assignee_id
      if (!isDirect && !isMyTeamUnclaimed) return false
    }
    const q = search.toLowerCase().trim()
    if (q && !t.title.toLowerCase().includes(q) && !t.sites?.name?.toLowerCase().includes(q) && !t.ticket_number.toLowerCase().includes(q)) return false
    return true
  })

  // The DB query orders by (priority_rank DESC, created_at DESC), so in 'priority'
  // mode we can use `filtered` directly. In 'recent' mode we re-sort client-side
  // by created_at DESC, ignoring priority_rank — useful for surfacing newly
  // auto-created audit-finding tickets which default to priority_rank=0.
  const sorted = sortMode === 'recent'
    ? [...filtered].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    : filtered

  const stats = {
    total:    tickets.filter(t => activeStatuses.includes(t.status)).length,
    critical: tickets.filter(t => t.tier === 'Critical' && activeStatuses.includes(t.status)).length,
    dispatched: tickets.filter(t => t.status === 'dispatched').length,
    blocked:  tickets.filter(t => t.status === 'blocked').length,
    // Used purely to badge the "Mine" button with a live count of my open
    // tickets. Mirrors the filter logic above: direct assignment OR unclaimed
    // on my team.
    mineOpen: profile?.id
      ? tickets.filter(t => {
          if (!activeStatuses.includes(t.status)) return false
          if (t.assignee_id === profile.id) return true
          if (profile.team && t.assignee_team === profile.team && !t.assignee_id) return true
          return false
        }).length
      : 0,
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Tickets</div>
          <div className="page-subtitle">All open issues across programs</div>
        </div>
        {canCreate && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowNewModal(true)}>
            + New ticket
          </button>
        )}
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-number">{stats.total}</div><div className="stat-label">Open</div></div>
        <div className="stat-card"><div className="stat-number" style={{ color: stats.critical > 0 ? 'var(--red)' : undefined }}>{stats.critical}</div><div className="stat-label">Critical</div></div>
        <div className="stat-card"><div className="stat-number" style={{ color: 'var(--amber)' }}>{stats.dispatched}</div><div className="stat-label">Dispatched</div></div>
        <div className="stat-card"><div className="stat-number" style={{ color: 'var(--red)' }}>{stats.blocked}</div><div className="stat-label">Blocked</div></div>
      </div>

      <input
        className="form-input"
        placeholder="Search by ticket #, site, or title..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10, overflowX: 'auto' }}>
        <button className={`btn btn-sm ${programFilter === 'all' ? 'btn-primary' : ''}`} onClick={() => setProgramFilter('all')}>All programs</button>
        {programs.map(p => (
          <button
            key={p.id}
            className={`btn btn-sm ${programFilter === p.slug ? 'btn-primary' : ''}`}
            onClick={() => setProgramFilter(p.slug)}>
            {p.slug === 'pa-phase1' ? 'PA-1' : p.slug === 'amp-migration' ? 'AMP' : 'Main'}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
        {['all','Critical','High','Medium','Low','AMP'].map(t => {
          const style = t === 'all' ? null : TIER_STYLE[t.toLowerCase()]
          return (
            <button
              key={t}
              className={`btn btn-sm ${tierFilter === t ? 'btn-primary' : ''}`}
              onClick={() => setTierFilter(t)}>
              {t === 'all' ? 'All tiers' : `${style?.dot || ''} ${t}`}
            </button>
          )
        })}
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className={`btn btn-sm ${statusFilter === 'open' ? 'btn-primary' : ''}`} onClick={() => setStatusFilter('open')}>Open</button>
        <button className={`btn btn-sm ${statusFilter === 'resolved' ? 'btn-primary' : ''}`} onClick={() => setStatusFilter('resolved')}>Resolved</button>
        <button className={`btn btn-sm ${statusFilter === 'all' ? 'btn-primary' : ''}`} onClick={() => setStatusFilter('all')}>All</button>

        {profile?.id && (
          <button
            className={`btn btn-sm ${assigneeFilter === 'mine' ? 'btn-primary' : ''}`}
            onClick={() => setAssigneeFilter(assigneeFilter === 'mine' ? 'all' : 'mine')}
            title="Show only tickets assigned to you">
            👤 Mine{stats.mineOpen > 0 && ` (${stats.mineOpen})`}
          </button>
        )}

        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted)' }}>Sort:</span>
        <button className={`btn btn-sm ${sortMode === 'priority' ? 'btn-primary' : ''}`} onClick={() => setSortMode('priority')}>Priority</button>
        <button className={`btn btn-sm ${sortMode === 'recent' ? 'btn-primary' : ''}`} onClick={() => setSortMode('recent')}>Recent</button>
      </div>

      {sorted.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📬</div>
          <h3>No tickets match</h3>
          <p>Try clearing filters or searching for something else.</p>
        </div>
      ) : (
        <>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
            {sorted.length} of {tickets.length} tickets
          </div>
          {sorted.map(t => (
            <TicketRow key={t.id} t={t} onClick={() => navigate(`/tickets/${t.id}`)} />
          ))}
        </>
      )}

      {showNewModal && (
        <NewTicketModal
          sites={sites}
          programs={programs}
          onSave={createTicket}
          onClose={() => setShowNewModal(false)}
        />
      )}
    </div>
  )
}

function TicketRow({ t, onClick }) {
  const navigate = useNavigate()
  const ss = STATUS_STYLE[t.status] || STATUS_STYLE.open
  const programSlug = t.programs?.slug
  // null program = not yet triaged (this happens for audit-generated tickets by design).
  const programShort = !programSlug ? null
    : programSlug === 'pa-phase1'     ? 'PA-1'
    : programSlug === 'amp-migration' ? 'AMP'
    :                                   'Main'
  const fromAudit = t.source === 'audit_finding'
  const age = ageDays(t.created_at)
  return (
    <div className="card" style={{ marginBottom: 8, cursor: 'pointer' }} onClick={onClick}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>{t.ticket_number}</span>
            <TierBadge tier={t.tier} />
            {programShort ? (
              <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: 'var(--gray-light)', color: 'var(--gray)', fontWeight: 500 }}>
                {programShort}
              </span>
            ) : (
              <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: '#FAEEDA', color: '#854F0B', fontWeight: 500 }}>
                Needs triage
              </span>
            )}
            {fromAudit && (
              <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: '#E6F1FB', color: '#185FA5', fontWeight: 500 }}>
                🔍 From audit
              </span>
            )}
            {t.type && (
              <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: 'var(--bg-secondary)', color: 'var(--text-muted)' }}>
                {t.type}
              </span>
            )}
          </div>
          <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{t.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {t.sites?.id ? (
              <span
                onClick={e => { e.stopPropagation(); navigate(`/sites/${t.sites.id}`) }}
                style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                {t.sites.name}
              </span>
            ) : 'Unknown site'}
            {' · '}{t.assignee_team || 'Unassigned'} · {age}
          </div>
        </div>
        <span style={{
          fontSize: 11, padding: '3px 9px', borderRadius: 10,
          background: ss.bg, color: ss.color, fontWeight: 500, flexShrink: 0,
          textTransform: 'capitalize',
        }}>
          {t.status.replace('_',' ')}
        </span>
      </div>
    </div>
  )
}

function ageDays(createdAt) {
  if (!createdAt) return ''
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / 86400000)
  if (days === 0) return 'Today'
  if (days === 1) return '1d'
  return `${days}d`
}

// Pulled from the tickets table CHECK constraints in 20260421_programs_system.sql.
const TIER_OPTIONS = ['Critical', 'High', 'Medium', 'Low', 'AMP']
const TYPE_OPTIONS = ['Hardware', 'Software', 'Connectivity', 'Data', 'Configuration', 'Process', 'Other']
// 'assignee_team' is a free-text column; these are the values used by the
// seeder so stay consistent for filtering/grouping elsewhere in the app.
const TEAM_OPTIONS = ['field_service', 'remote_team', 'migration_pm', 'ncs', 'nxt_product', 'ops']

function NewTicketModal({ sites, programs, onSave, onClose }) {
  const [form, setForm] = useState({
    site_id: '',
    program_id: '',
    title: '',
    description: '',
    tier: '',
    type: '',
    assignee_team: '',
  })
  const [images, setImages] = useState([])  // File[] queued for upload post-create
  const [saving, setSaving] = useState(false)

  const canSave = form.site_id && form.title.trim().length > 0 && !saving

  function addImages(fileList) {
    // Accept FileList or array; filter to images only. Dedup naively by
    // name+size+lastModified so the user can hit the picker twice without
    // doubling up their last selection.
    const incoming = Array.from(fileList || []).filter(f => f.type.startsWith('image/'))
    setImages(prev => {
      const key = f => `${f.name}:${f.size}:${f.lastModified}`
      const seen = new Set(prev.map(key))
      return [...prev, ...incoming.filter(f => !seen.has(key(f)))]
    })
  }

  function removeImage(i) {
    setImages(prev => prev.filter((_, idx) => idx !== i))
  }

  async function handleSave() {
    if (!canSave) return
    setSaving(true)
    try { await onSave(form, images) } finally { setSaving(false) }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 540 }}>
        <div className="modal-title">New ticket</div>

        <div className="form-group">
          <label className="form-label">Site *</label>
          <select className="form-input" value={form.site_id}
            onChange={e => setForm(p => ({ ...p, site_id: e.target.value }))}>
            <option value="">Select a site…</option>
            {sites.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}{s.city ? ` — ${s.city}${s.state ? ', ' + s.state : ''}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Title *</label>
          <input className="form-input" value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            placeholder="e.g. LPR camera offline at entry lane" />
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-input" rows={3} value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="What's happening, what's been tried, anything useful for the tech…" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="form-group">
            <label className="form-label">Program</label>
            <select className="form-input" value={form.program_id}
              onChange={e => setForm(p => ({ ...p, program_id: e.target.value }))}>
              <option value="">None / triage later</option>
              {programs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Tier</label>
            <select className="form-input" value={form.tier}
              onChange={e => setForm(p => ({ ...p, tier: e.target.value }))}>
              <option value="">—</option>
              {TIER_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="form-group">
            <label className="form-label">Type</label>
            <select className="form-input" value={form.type}
              onChange={e => setForm(p => ({ ...p, type: e.target.value }))}>
              <option value="">—</option>
              {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Assignee team</label>
            <select className="form-input" value={form.assignee_team}
              onChange={e => setForm(p => ({ ...p, assignee_team: e.target.value }))}>
              <option value="">Unassigned</option>
              {TEAM_OPTIONS.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">
            Images {images.length > 0 && <span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>({images.length})</span>}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {/* Previews — Object URLs revoked on unmount via effect would be
                tidy, but modal instances are short-lived and the leak is bounded
                by one session. Keep it simple. */}
            {images.map((f, i) => {
              const url = URL.createObjectURL(f)
              return (
                <div key={`${f.name}-${i}`} style={{ position: 'relative' }}>
                  <img
                    src={url}
                    alt={f.name}
                    style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }}
                  />
                  <button
                    type="button"
                    aria-label="Remove image"
                    onClick={() => removeImage(i)}
                    style={{
                      position: 'absolute', top: -6, right: -6,
                      width: 18, height: 18, borderRadius: 9,
                      background: '#333', color: 'white',
                      border: 'none', fontSize: 11, lineHeight: 1,
                      cursor: 'pointer', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>×</button>
                </div>
              )
            })}
            <label className="btn btn-sm" style={{ cursor: 'pointer' }}>
              + Add image
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => {
                  addImages(e.target.files)
                  e.target.value = ''
                }}
              />
            </label>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Optional. Images upload after the ticket is created.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={handleSave}>
            {saving ? 'Creating…' : 'Create ticket'}
          </button>
        </div>
      </div>
    </div>
  )
}
